/** Shared by Studio Playground and Beat Arranger. No upload or AI calls. */
const BASE = process.env.REACT_APP_BASS_SOUNDS_URL || '/bass-sounds';
let catalogPromise;
const buffers = new Map();
export function loadBassCatalog() {
  if (!catalogPromise) catalogPromise = fetch(`${BASE}/manifest.json`).then(r => {
    if (!r.ok) throw new Error('Bass sounds could not load. Try again.');
    return r.json();
  }).catch(e => {
    catalogPromise = null;
    throw e;
  });
  return catalogPromise;
}
export async function loadBassSound(ctx, id) {
  if (!id) return null;
  if (!buffers.has(id)) buffers.set(id, (async () => {
    const catalog = await loadBassCatalog();
    const sound = catalog.sounds.find(s => s.id === id);
    if (!sound) throw new Error('This bass sound is missing. Choose another sound.');
    const response = await fetch(`${BASE}/${sound.file}`);
    if (!response.ok) throw new Error('Bass sound could not load. Try again.');
    return {
      ...sound,
      buffer: await ctx.decodeAudioData(await response.arrayBuffer())
    };
  })().catch(e => {
    buffers.delete(id);
    throw e;
  }));
  return buffers.get(id);
}
export const midiFrequency = midi => 440 * 2 ** ((midi - 69) / 12);
export function cleanBassNotes(notes, bars = 4) {
  return (notes || []).filter(n => Number.isFinite(+n.step) && +n.step >= 0 && +n.step < bars * 16 && Number.isFinite(+n.frequency) && +n.frequency > 0).map((n, i) => ({
    ...n,
    id: n.id || `bass-note-${i}`,
    step: +n.step,
    durationSteps: Math.max(.0625, Math.min(bars * 16 - +n.step, +n.durationSteps || 1)),
    velocity: Math.max(.1, Math.min(1, +n.velocity || .8)),
    ...(Object.prototype.hasOwnProperty.call(n, 'glideMs') ? {
      glideMs: Math.max(0, Math.min(500, +n.glideMs || 0))
    } : {})
  })).sort((a, b) => a.step - b.step).filter((note, index, all) => index === all.length - 1 || all[index + 1].step !== note.step);
}
/** Only intentional incoming glide connects touching notes. Legacy random slide flags are ignored. */
export function bassGlide(note, previous, stepSeconds) {
  const connected = previous && previous.step + previous.durationSteps >= note.step - .001 && previous.step < note.step;
  return connected && note.glideMs > 0 ? {
    from: previous.frequency,
    seconds: Math.min(note.glideMs / 1000, note.durationSteps * stepSeconds * .8)
  } : null;
}
export function scheduleBassNote(ctx, destination, note, previous, when, duration, settings = {}, stepSeconds = .125, next = null) {
  const sample = settings.sample;
  const source = sample ? ctx.createBufferSource() : ctx.createOscillator();
  const glide = bassGlide(note, previous, stepSeconds);
  const pitch = glide?.from || note.frequency;
  const parameter = sample ? source.playbackRate : source.frequency;
  const convert = frequency => sample ? frequency / midiFrequency(sample.rootMidi) : frequency;
  if (sample) source.buffer = sample.buffer;else source.type = settings.oscType || 'sine';
  parameter.setValueAtTime(convert(pitch), when);
  if (glide) parameter.exponentialRampToValueAtTime(convert(note.frequency), when + glide.seconds);
  if (!sample && settings.detune) source.detune.setValueAtTime(settings.detune, when);
  const gain = ctx.createGain();
  const level = note.velocity ?? .8;
  gain.gain.setValueAtTime(0, when);
  gain.gain.linearRampToValueAtTime(level, when + Math.min(.005, duration / 3));
  gain.gain.setValueAtTime(level, when + Math.max(duration / 3, duration - .015));
  gain.gain.linearRampToValueAtTime(0, when + duration);
  if (sample) source.connect(gain);else {
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(settings.filterFreq || 300, when);
    filter.Q.value = 1;
    source.connect(filter);
    filter.connect(gain);
  }
  gain.connect(destination);
  source.start(when);
  source.stop(when + duration + .002);
  return source;
}
export async function renderBass({
  pattern,
  bpm,
  bars = 4,
  volume = .8,
  sampleId,
  ...settings
}) {
  const Context = globalThis.OfflineAudioContext || globalThis.webkitOfflineAudioContext;
  const ctx = new Context(2, Math.ceil(bars * 240 / bpm * 44100), 44100);
  const master = ctx.createGain();
  master.gain.value = volume;
  master.connect(ctx.destination);
  const sample = await loadBassSound(ctx, sampleId);
  const notes = cleanBassNotes(pattern, bars);
  const stepSeconds = 60 / bpm / 4;
  notes.forEach((note, i) => {
    const next = notes[i + 1];
    const steps = Math.min(note.durationSteps, next ? Math.max(.0625, next.step - note.step) : bars * 16 - note.step);
    scheduleBassNote(ctx, master, note, notes[i - 1], note.step * stepSeconds, steps * stepSeconds, {
      ...settings,
      sample
    }, stepSeconds, next);
  });
  return ctx.startRendering();
}
